import { useEffect, useMemo, useState } from 'react';
import { api, actualizar } from '../api/cliente';
import type { ComparacionOdontograma, OdontogramaVista, RegistroOdontograma } from '../api/tipos';
import {
  Aviso,
  Boton,
  Cargando,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { fechaYHora, legible } from '../comun/formato';
import { LeyendaOdontograma, Odontograma, Paleta } from './Odontograma';
import { ESTADOS_PIEZA, aRegistros, indexar, nombreDeEstado } from './modeloOdontograma';
import type { Denticion, Superficie } from './modeloOdontograma';
import { useSesion } from '../sesion/Sesion';

/**
 * Odontograma del paciente: ver, comparar y editar. Cada guardado crea una versión nueva
 * —el histórico nunca se sobrescribe— y el servidor calcula la diferencia contra la
 * anterior, que es lo que se muestra al confirmar.
 */
export function OdontogramaDelPaciente({ pacienteId }: { pacienteId: string }) {
  const { puede } = useSesion();
  const editable = puede('odontograma.escribir');

  const { datos, cargando, error, recargar } = useDatos<OdontogramaVista>(
    `/api/v1/pacientes/${pacienteId}/odontograma`,
  );

  const [borrador, setBorrador] = useState<RegistroOdontograma[] | null>(null);
  const [capa, setCapa] = useState<'Hallazgo' | 'Plan'>('Hallazgo');
  const [pincel, setPincel] = useState<string | null>(null);
  const [notacion, setNotacion] = useState<'FDI' | 'Universal'>('FDI');
  const [denticion, setDenticion] = useState<Denticion>('Permanente');
  const [pieza, setPieza] = useState<string | null>(null);
  const [comparacion, setComparacion] = useState<ComparacionOdontograma | null>(null);
  const { correr, ejecutando, error: errorGuardado } = useAccion();

  useEffect(() => {
    if (datos) {
      setBorrador(null);
      setDenticion((datos.denticion as Denticion) ?? 'Permanente');
    }
  }, [datos]);

  const registros = borrador ?? datos?.registros ?? [];
  const hayCambios = borrador != null;

  const pintar = (piezaTocada: string, superficie: Superficie) => {
    if (!pincel) {
      setPieza(piezaTocada);

      return;
    }

    const mapa = indexar(registros);
    const entrada = mapa.get(piezaTocada) ?? {
      hallazgo: { superficies: {}, pieza: null, notas: [] },
      plan: { superficies: {}, pieza: null, notas: [] },
    };

    const destino = capa === 'Plan' ? entrada.plan : entrada.hallazgo;

    // Tocar dos veces la misma superficie con el mismo estado la borra: es el «deshacer»
    // inmediato que el encargo prefiere sobre un diálogo de confirmación.
    if (destino.superficies[superficie] === pincel) {
      delete destino.superficies[superficie];
    } else {
      destino.superficies[superficie] = pincel;
    }

    mapa.set(piezaTocada, entrada);
    setBorrador(aRegistros(mapa));
  };

  const marcarPieza = (piezaTocada: string, estado: string | null) => {
    const mapa = indexar(registros);
    const entrada = mapa.get(piezaTocada) ?? {
      hallazgo: { superficies: {}, pieza: null, notas: [] },
      plan: { superficies: {}, pieza: null, notas: [] },
    };

    const destino = capa === 'Plan' ? entrada.plan : entrada.hallazgo;
    destino.pieza = destino.pieza === estado ? null : estado;

    mapa.set(piezaTocada, entrada);
    setBorrador(aRegistros(mapa));
  };

  const guardar = async () => {
    const resultado = await correr(() =>
      actualizar<{ version: number }>(`/api/v1/pacientes/${pacienteId}/odontograma`, {
        pacienteId,
        motivo: 'Revision',
        registros: borrador,
      }),
    );

    if (resultado) {
      const diferencia = await api<ComparacionOdontograma>(
        `/api/v1/pacientes/${pacienteId}/odontograma/comparar?versionDesde=${resultado.version - 1}&versionHasta=${resultado.version}`,
      ).catch(() => null);

      setComparacion(diferencia);
      setBorrador(null);
      setPincel(null);
      recargar();
    }
  };

  const estadoDePieza = useMemo(() => {
    if (!pieza) {
      return null;
    }

    const mapa = indexar(registros);

    return mapa.get(pieza)?.[capa === 'Plan' ? 'plan' : 'hallazgo'] ?? null;
  }, [pieza, registros, capa]);

  if (cargando) return <Cargando que="el odontograma" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  return (
    <div className="space-y-4">
      {comparacion && comparacion.cambios.length > 0 && (
        <Aviso tono="exito" alCerrar={() => setComparacion(null)}>
          Se guardó la versión {comparacion.versionNueva} con {comparacion.cambios.length}{' '}
          {comparacion.cambios.length === 1 ? 'cambio' : 'cambios'} en{' '}
          {comparacion.piezasAfectadas.join(', ')}.
        </Aviso>
      )}

      <Panel
        titulo={datos ? `Odontograma · versión ${datos.version}` : 'Odontograma'}
        descripcion={
          datos
            ? `${legible(datos.motivo)} · ${fechaYHora(datos.registradaEn)} · notación ${notacion}`
            : undefined
        }
        accion={
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-dato text-tinta-500">
              <span className="sr-only">Notación</span>
              <select
                value={notacion}
                onChange={(e) => setNotacion(e.target.value as 'FDI' | 'Universal')}
                className="h-tactil rounded-lg border border-papel-300 bg-white px-2 text-sm"
              >
                <option value="FDI">FDI / ISO</option>
                <option value="Universal">Universal</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-dato text-tinta-500">
              <span className="sr-only">Dentición</span>
              <select
                value={denticion}
                onChange={(e) => setDenticion(e.target.value as Denticion)}
                className="h-tactil rounded-lg border border-papel-300 bg-white px-2 text-sm"
              >
                <option value="Permanente">Permanente</option>
                <option value="Temporaria">Temporaria</option>
                <option value="Mixta">Mixta</option>
              </select>
            </label>
          </div>
        }
      >
        {!datos && (
          <Vacio
            titulo="Todavía no hay odontograma"
            detalle="Marcá los hallazgos y guardá: se crea la primera versión y el historial arranca ahí."
          />
        )}

        <Odontograma
          registros={registros}
          denticion={denticion}
          capa={capa}
          notacion={notacion}
          pincel={editable ? pincel : null}
          piezaSeleccionada={pieza}
          alTocarSuperficie={editable ? pintar : undefined}
          alTocarPieza={setPieza}
        />
      </Panel>

      <div className="grid gap-4 portatil:grid-cols-3">
        {editable && (
          <Panel className="portatil:col-span-2" titulo="Marcar">
            <Paleta
              capa={capa}
              alCambiarCapa={setCapa}
              pincel={pincel}
              alCambiarPincel={setPincel}
            />

            {hayCambios && (
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-papel-200 pt-4">
                <Boton variante="primario" cargando={ejecutando} onClick={() => void guardar()}>
                  Guardar versión nueva
                </Boton>
                <Boton onClick={() => setBorrador(null)}>Descartar cambios</Boton>
                <span className="text-dato text-tinta-500">
                  La versión anterior queda intacta en el historial.
                </span>
              </div>
            )}

            {errorGuardado && (
              <Aviso tono="alerta" className="mt-3">
                {errorGuardado}
              </Aviso>
            )}
          </Panel>
        )}

        <div className="space-y-4">
          {pieza && (
            <Panel titulo={`Pieza ${pieza}`}>
              {estadoDePieza && estadoDePieza.pieza && (
                <p className="mb-3 text-sm text-tinta-700">
                  Estado: <strong>{nombreDeEstado(estadoDePieza.pieza)}</strong>
                </p>
              )}

              {estadoDePieza && Object.keys(estadoDePieza.superficies).length > 0 ? (
                <ul className="mb-3 space-y-1">
                  {Object.entries(estadoDePieza.superficies).map(([superficie, estado]) => (
                    <li key={superficie} className="text-dato text-tinta-600">
                      {superficie}: {nombreDeEstado(estado as string)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mb-3 text-dato text-tinta-500">Sin hallazgos en esta capa.</p>
              )}

              {editable && (
                <>
                  <p className="etiqueta-campo mb-2">Estado de la pieza completa</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ESTADOS_PIEZA).map(([codigo, estado]) => (
                      <button
                        key={codigo}
                        type="button"
                        onClick={() => marcarPieza(pieza, codigo)}
                        aria-pressed={estadoDePieza?.pieza === codigo}
                        className={clases(
                          'inline-flex min-h-tactil items-center gap-2 rounded-lg border px-3 text-sm transition-colors',
                          estadoDePieza?.pieza === codigo
                            ? 'border-marca-600 bg-marca-50 text-marca-700 ring-1 ring-marca-600'
                            : 'border-papel-300 bg-white text-tinta-600 hover:bg-papel-50',
                        )}
                      >
                        {estado.nombre}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <Boton className="mt-3" onClick={() => setPieza(null)}>
                Cerrar
              </Boton>
            </Panel>
          )}

          <LeyendaOdontograma />

          {datos && datos.version > 1 && <Historial pacienteId={pacienteId} version={datos.version} />}
        </div>
      </div>
    </div>
  );
}

function Historial({ pacienteId, version }: { pacienteId: string; version: number }) {
  const [desde, setDesde] = useState(version - 1);
  const { datos, cargando } = useDatos<ComparacionOdontograma>(
    `/api/v1/pacientes/${pacienteId}/odontograma/comparar?versionDesde=${desde}&versionHasta=${version}`,
    [desde, version],
  );

  return (
    <Panel titulo="Qué cambió" descripcion={`Contra la versión ${version}`}>
      <Selector
        etiqueta="Comparar con la versión"
        value={desde}
        onChange={(e) => setDesde(Number(e.target.value))}
      >
        {Array.from({ length: version - 1 }, (_, i) => i + 1).map((numero) => (
          <option key={numero} value={numero}>
            Versión {numero}
          </option>
        ))}
      </Selector>

      {cargando && <Cargando que="la comparación" />}

      {datos && datos.cambios.length === 0 && (
        <p className="mt-3 text-dato text-tinta-500">No hubo cambios entre esas versiones.</p>
      )}

      {datos && datos.cambios.length > 0 && (
        <ul className="mt-3 space-y-2">
          {datos.cambios.map((cambio, indice) => (
            <li key={indice} className="flex items-start gap-2 text-dato">
              <Insignia
                tono={
                  cambio.tipo === 'Agregado' ? 'exito' : cambio.tipo === 'Removido' ? 'alerta' : 'aviso'
                }
              >
                {cambio.pieza}
                {cambio.superficie ? ` ${cambio.superficie}` : ''}
              </Insignia>
              <span className="text-tinta-600">
                {cambio.anterior ? nombreDeEstado(cambio.anterior) : 'sin registro'} →{' '}
                {cambio.nuevo ? nombreDeEstado(cambio.nuevo) : 'sin registro'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
